// Sugalarm — web command-station left nav rail (cycle 170).
//
// WEB FRAME ONLY (rendered exclusively from WebFrame, which KioskFrame
// delegates to when variant === "web"). The Sugalarm shield logo sits at
// the top, then two grouped nav sections (Monitor / Manage) that map to
// the EXISTING routes (plus the new Dashboard + a Reports placeholder),
// and the signed-in account owner in the footer ("who am I" — distinct
// from the viewed member shown on the status strip / Dashboard header).
// Kiosk + mobile keep their own nav chrome (EdgeRail / FooterTabs /
// MobileFrame rail) untouched.
//
// Cycle 348: the web light/dark theme toggle that used to sit in the
// footer here is retired — web is permanently dark now.

import { NavLink } from "react-router-dom";

import type { User } from "../../lib/types";
import { Logo } from "../ui/Logo";
import styles from "./WebNavRail.module.css";

type RailIconId =
  | "dashboard"
  | "now"
  | "history"
  | "logbook"
  | "household"
  | "alarms"
  | "settings"
  | "reports";

interface NavItem {
  to: string;
  label: string;
  icon: RailIconId;
  /** Treat `/` and `/now` as the same destination for active matching. */
  end?: boolean;
}

const MONITOR: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
  { to: "/now", label: "Now", icon: "now" },
  { to: "/history", label: "History", icon: "history" },
  { to: "/log", label: "Logbook", icon: "logbook" },
];
const MANAGE: NavItem[] = [
  { to: "/household", label: "Household", icon: "household" },
  { to: "/alerts", label: "Alarms", icon: "alarms" },
  { to: "/settings", label: "Settings", icon: "settings" },
  { to: "/reports", label: "Reports", icon: "reports" },
];

interface Props {
  user: User | null;
  /** Household-wide unacknowledged alert count → a badge on the Alarms item. */
  unackCount: number;
}

export function WebNavRail({ user, unackCount }: Props) {
  const ownerLabel = user?.email ?? "Account";
  return (
    <nav className={styles.rail} aria-label="Primary">
      <div className={styles.brand}>
        <Logo size={34} />
        <span className={styles.brandWord}>Sugar Alarm</span>
      </div>

      <div className={styles.groups}>
        <NavGroup title="Monitor" items={MONITOR} unackCount={unackCount} />
        <NavGroup title="Manage" items={MANAGE} unackCount={unackCount} />
      </div>

      <div className={styles.footer}>
        {/* Cycle 348: the Light/Dark toggle is retired — web is
            permanently dark now, so there is nothing to toggle.
            WebThemeToggle.tsx + lib/webTheme.ts are left in place,
            unwired, per the operator's explicit "don't tear it out this
            cycle" direction — a future cleanup cycle's call. */}
        <div className={styles.owner} title={ownerLabel}>
          <span className={styles.ownerDot} aria-hidden>
            <RailIcon id="person" />
          </span>
          <span className={styles.ownerEmail}>{ownerLabel}</span>
        </div>
      </div>
    </nav>
  );
}

function NavGroup({
  title,
  items,
  unackCount,
}: {
  title: string;
  items: NavItem[];
  unackCount: number;
}) {
  return (
    <div className={styles.group}>
      <div className={styles.groupTitle}>{title}</div>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            `${styles.item} ${isActive ? styles.itemActive : ""}`
          }
        >
          <span className={styles.itemIcon} aria-hidden>
            <RailIcon id={it.icon} />
          </span>
          <span className={styles.itemLabel}>{it.label}</span>
          {it.icon === "alarms" && unackCount > 0 ? (
            <span className={styles.badge} aria-label={`${unackCount} unacknowledged`}>
              {unackCount > 99 ? "99+" : unackCount}
            </span>
          ) : null}
        </NavLink>
      ))}
    </div>
  );
}

// Hand-rolled inline glyphs (the app ships no icon dependency — the
// NavIcon convention). 24×24, currentColor stroke so they inherit the
// item's ink and re-tint with the theme.
function RailIcon({ id }: { id: RailIconId | "person" }) {
  const p = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (id) {
    case "dashboard":
      return (
        <svg {...p}>
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </svg>
      );
    case "now":
      return (
        <svg {...p}>
          <path d="M3 12h4l2.5 6 5-14 2.5 8H21" />
        </svg>
      );
    case "history":
      return (
        <svg {...p}>
          <path d="M3 12a9 9 0 1 0 3-6.7M3 4v4h4" />
          <path d="M12 8v4l3 2" />
        </svg>
      );
    case "logbook":
      return (
        <svg {...p}>
          <path d="M5 4h12a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2z" />
          <path d="M9 8h6M9 12h6M9 16h4" />
        </svg>
      );
    case "household":
      return (
        <svg {...p}>
          <path d="M9 21V9l-6 4M9 9l6-4 6 4v12" />
          <path d="M3 21h18" />
        </svg>
      );
    case "alarms":
      return (
        <svg {...p}>
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
      );
    case "settings":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case "reports":
      return (
        <svg {...p}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5M9 13l2 2 3-4" />
        </svg>
      );
    case "person":
      return (
        <svg {...p}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      );
    default:
      return null;
  }
}
